import { BrowserRouter, Routes, Route, Outlet } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import MobileNav from './components/layout/MobileNav';
import ProtectedRoute, { GuestRoute } from './components/shared/ProtectedRoute';
import PageTransition from './components/shared/PageTransition';

// Pages
import Home from './pages/Home';
import Browse from './pages/Browse';
import RaffleDetail from './pages/RaffleDetail';
import CompetitionManage from './pages/CompetitionManage';
import EntrySuccess from './pages/EntrySuccess';
import HowItWorks from './pages/HowItWorks';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import About from './pages/About';
import NotFound from './pages/NotFound';

// Auth pages
import SignUp from './pages/auth/SignUp';
import LogIn from './pages/auth/LogIn';
import VerifyEmail from './pages/auth/VerifyEmail';
import VerifyPhone from './pages/auth/VerifyPhone';
import ResetPassword from './pages/auth/ResetPassword';

// Seller pages
import SellerOnboarding from './pages/seller/SellerOnboarding';
import CreateRaffle from './pages/seller/CreateRaffle';
import CreateSuccess from './pages/seller/CreateSuccess';

// User management pages
import Dashboard from './pages/Dashboard';
import Settings from './pages/Settings';
import MyEntries from './pages/MyEntries';
import MyCompetitions from './pages/MyCompetitions';

// Public profile pages
import SellerProfile from './pages/SellerProfile';

// Admin pages
import DisputeList from './pages/admin/DisputeList';
import DisputeDetail from './pages/admin/DisputeDetail';

/**
 * Main layout with Navbar, Footer, and Mobile Nav
 */
function MainLayout() {
  return (
    <div className="min-h-screen flex flex-col bg-midnight">
      <Navbar />
      <main className="flex-1 pb-mobile-nav">
        <PageTransition>
          <Outlet />
        </PageTransition>
      </main>
      <Footer className="hidden md:block" />
      <MobileNav />
    </div>
  );
}

/**
 * Auth layout - no Navbar/Footer
 */
function AuthLayout() {
  return (
    <PageTransition>
      <Outlet />
    </PageTransition>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <Routes>
            {/* Auth routes (no navbar/footer) */}
            <Route element={<AuthLayout />}>
              <Route
                path="/auth/signup"
                element={
                  <GuestRoute>
                    <SignUp />
                  </GuestRoute>
                }
              />
              <Route
                path="/auth/login"
                element={
                  <GuestRoute>
                    <LogIn />
                  </GuestRoute>
                }
              />
              <Route
                path="/auth/verify-email"
                element={
                  <ProtectedRoute requireAuth requireEmailVerified={false}>
                    <VerifyEmail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/auth/verify-phone"
                element={
                  <ProtectedRoute requireAuth requireEmailVerified>
                    <VerifyPhone />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/auth/reset-password"
                element={<ResetPassword />}
              />
            </Route>

            {/* Seller routes (no navbar/footer - full page) */}
            <Route element={<AuthLayout />}>
              <Route
                path="/seller/onboarding"
                element={
                  <ProtectedRoute requireAuth requireEmailVerified requirePhoneVerified>
                    <SellerOnboarding />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/create"
                element={
                  <ProtectedRoute requireAuth requireEmailVerified requirePhoneVerified>
                    <CreateRaffle />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/create/success"
                element={
                  <ProtectedRoute requireAuth requireEmailVerified requirePhoneVerified>
                    <CreateSuccess />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/competition/:id/manage"
                element={
                  <ProtectedRoute requireAuth>
                    <CompetitionManage />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Main app routes (with navbar/footer) */}
            <Route element={<MainLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/browse" element={<Browse />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/about" element={<About />} />
              <Route path="/competition/:id" element={<RaffleDetail />} />
              <Route path="/seller/:userId" element={<SellerProfile />} />
              <Route
                path="/competition/:id/entry-success"
                element={
                  <ProtectedRoute requireAuth>
                    <EntrySuccess />
                  </ProtectedRoute>
                }
              />
              {/* User management routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute requireAuth>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute requireAuth>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-entries"
                element={
                  <ProtectedRoute requireAuth>
                    <MyEntries />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-competitions"
                element={
                  <ProtectedRoute requireAuth>
                    <MyCompetitions />
                  </ProtectedRoute>
                }
              />
              {/* Admin routes */}
              <Route
                path="/admin/disputes"
                element={
                  <ProtectedRoute requireAuth requireAdmin>
                    <DisputeList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/disputes/:id"
                element={
                  <ProtectedRoute requireAuth requireAdmin>
                    <DisputeDetail />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
